import React, {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Badge, Button, DatePicker, Pagination, Select, Table, Tag, message} from 'antd'
import moment from 'moment'
import queryString from 'query-string'
import {getOutputRecords} from '../../../../services/api/apiProductionPlanning'
import {OUTPUT_STATUS_LABELS, OWNERS, ROUTES} from '../../constants'
import {formatDateTime, timezoneLabelForRange, formatInt} from '../../helpers/format'
import {vnDayRangeIso} from '../../helpers/dateRange'
import useUrlFilters from '../../../helpers/useUrlFilters'
import Notice from '../shared/Notice'
import LoadError from '../shared/LoadError'
import Duration from '../shared/Duration'
import TimeAgo from '../shared/TimeAgo'

const {Option} = Select

/** Nội dung hộp giải thích, đổi theo bộ lọc đang áp (§C.2 ③). */
const EXPLAIN = {
    no_standard: {
        level: 'warning',
        text: `Đây là danh sách việc của ${OWNERS.standard}. Sản lượng đã làm thật, không bị tính bằng 0 — chỉ là chưa quy đổi được ra giờ định mức.`,
    },
    no_file_data: {
        level: 'error',
        text: `Đây là danh sách việc của ${OWNERS.fileData}. Thiếu diện tích in, chiều dài cắt hoặc diện tích khắc nên chưa tính được giờ định mức.`,
    },
    redo: {
        level: 'info',
        text: 'Mọi lần đều được tính và ghi công, kể cả khi hai lần do hai tài khoản khác nhau làm.',
    },
    by_order: {
        level: 'info',
        text: 'Một đơn nhiều sản phẩm chỉ dán một label nên các dòng log cùng đơn gộp thành một sản lượng. Riêng Đóng thùng chỉ xảy ra khi toàn bộ sản phẩm của đơn đã xong, đã đóng gói, đã dán label — không tồn tại trường hợp từng sản phẩm vào thùng riêng.',
    },
    by_detect: {
        level: 'info',
        text: 'Mỗi dòng là một sản phẩm và một sản lượng, nhưng giờ định mức cộng từ tất cả dòng vật liệu của sản phẩm đó.',
    },
}

/**
 * SCR-M18-002 — Chi tiết sản lượng.
 *
 * Màn hình dùng để trả lời khi có người thắc mắc về số liệu, nên chỉ vào được bằng cách bấm một con
 * số ở SCR-M18-001 — không có mục menu riêng.
 *
 * Hai cấu trúc cột: đếm theo sản phẩm và đếm theo đơn hàng. Cột **Sản lượng luôn bằng 1** ở cấu
 * trúc 1 kể cả khi dòng có nhiều dòng vật liệu (C-AC-11) — bỏ cột này là mất luôn bằng chứng trực
 * quan rằng đơn vị đếm và cách tính giờ là hai việc tách rời.
 */
/** Chỉ `only` là bộ lọc của màn; from/to/actor/stageCode là tham số điều hướng, không khai ở đây. */
const FILTER_DEFAULTS = {only: ''}

/**
 * `embedded`/`params`/`onClose`: dùng khi OutputReportPage nhúng màn này vào Modal (width 95%) thay
 * vì điều hướng sang route riêng. Ở chế độ đó, tham số điều hướng đến từ props (modal mở NGAY TRÊN
 * url của báo cáo, không có actor/stageCode/from/to trên URL để đọc), và bộ lọc `only` giữ state
 * RIÊNG (không đồng bộ URL) — tránh đụng `useUrlFilters` của chính báo cáo đang chạy trên cùng URL.
 * Route độc lập `/control/output/detail` (routes/view.js, dev/QC test trực tiếp bằng URL) vẫn giữ
 * nguyên hành vi cũ khi không truyền các prop này.
 */
function OutputDetailPage({
    history, location, embedded, params: embeddedParams, onClose, onApplyFilters, stageOptions, actorOptions,
}) {
    const routeQuery = queryString.parse(location.search)
    const query = embedded ? (embeddedParams || {}) : routeQuery

    const [rows, setRows] = useState([])
    const [meta, setMeta] = useState(null)
    const [pagination, setPagination] = useState({page: 1, limit: 50, total: 0})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [localOnly, setLocalOnly] = useState((embeddedParams && embeddedParams.only) || '')
    // `only` là bộ lọc DUY NHẤT người dùng đổi được ở màn này (đóng thẻ để bỏ), nên cho nó lên URL —
    // CHỈ khi KHÔNG nhúng (route độc lập). Hook vẫn gọi vô điều kiện để không phá thứ tự hook; nhánh
    // embedded chỉ đơn giản là không dùng `urlFilters`/`setUrlFilter`.
    const [urlFilters, setUrlFilter] = useUrlFilters(FILTER_DEFAULTS, {history, location})
    const only = (embedded ? localOnly : urlFilters.only) || null
    const setOnly = embedded ? (value) => setLocalOnly(value || '') : (value) => setUrlFilter({only: value || ''})
    // Suy ra chứ không giữ state riêng: hai thứ này luôn đi cùng nhau, tách ra là có ngày lệch.
    const groupByBarcode = only === 'distinct'
    const closeAction = embedded ? onClose : () => history.goBack()

    /**
     * Bộ lọc SỬA ĐƯỢC ngay trên màn này (tài khoản/công đoạn/khoảng thời gian) — mặc định lấy từ
     * `query` (tham số điều hướng do báo cáo truyền sang), nhưng bấm "Áp dụng" thì đổi RIÊNG cho
     * màn này, không đụng bộ lọc của báo cáo phía sau. `draft*` chỉ là giá trị đang gõ/chọn trên ô,
     * `query.*` mới là bộ lọc ĐANG ÁP — đồng bộ lại `draft*` mỗi khi `query.*` đổi (bấm Áp dụng xong,
     * hoặc bấm Back/Forward của trình duyệt) để ô lọc không hiện giá trị cũ chưa áp.
     */
    /*
     * Nhãn tài khoản khi khoan sâu KHÔNG kèm tài khoản.
     *
     * Màn "Sản lượng theo công đoạn" (SCR-M18-005) bấm số ở dòng CHA = một công đoạn, mọi tài khoản,
     * nên `actor` cố ý rỗng. Không có nhánh này thì tiêu đề và thẻ lọc hiện một khoảng TRỐNG, trông
     * như dữ liệu hỏng chứ không như "đang xem tất cả".
     *
     * ⚠️ CỐ Ý không đọc `meta.actor`. Service ĐOÁN nó từ dòng đầu của CẢ TẬP kết quả:
     *
     *      production-performance · output-control.service.ts:490 · method `records()`
     *      actor: query.actor ?? filtered[0]?.actor ?? null
     *
     *    `filtered` là tập đã lọc TRƯỚC khi cắt trang (`filtered.slice(start, start + limit)` chỉ
     *    dựng `items`), nên nhãn KHÔNG đổi khi sang trang — nó chỉ đơn giản là tên của nhân viên
     *    đứng đầu toàn bộ kết quả.
     *    Lấy nó làm nhãn là gán số liệu của cả bảng cho đúng một người — mà bảng lại không có cột
     *    Tài khoản để ai đó nhận ra. `query.actor` là nguồn duy nhất đáng tin: nó chính là thứ ta
     *    gửi đi, nên nhãn luôn mô tả đúng bộ lọc đang áp.
     */
    const actorLabel = query.actor || 'Tất cả tài khoản'

    const [draftActor, setDraftActor] = useState(query.actor || '')
    const [draftStageCode, setDraftStageCode] = useState(query.stageCode || '')
    const [draftFrom, setDraftFrom] = useState(query.from ? moment(query.from) : null)
    const [draftTo, setDraftTo] = useState(query.to ? moment(query.to) : null)

    useEffect(() => {
        setDraftActor(query.actor || '')
        setDraftStageCode(query.stageCode || '')
        setDraftFrom(query.from ? moment(query.from) : null)
        setDraftTo(query.to ? moment(query.to) : null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.actor, query.stageCode, query.from, query.to])

    /*
     * Đang gõ/chọn khác với bộ lọc ĐANG ÁP (`query.*`) mà chưa bấm Áp dụng — cần báo rõ, không thì
     * người dùng nhìn ô đã đổi (vd đổi Tài khoản) nhưng thẻ + bảng dưới vẫn là dữ liệu CŨ, tưởng
     * màn hình bị treo hoặc đổi nhầm chỗ.
     *
     * ⚠️ So bằng MỐC, không so chuỗi. Bản trước so `draft.toISOString()` (`…Z`) với `query.from`,
     *    mà trang cha nay gửi khoảng dạng `…+07:00` (`vnDayRangeIso`) — hai chuỗi KHÔNG BAO GIỜ
     *    bằng nhau dù cùng một mốc, nên chấm "Có thay đổi chưa áp dụng" hiện thường trực mỗi lần
     *    khoan sâu. Một cảnh báo luôn bật là một cảnh báo người dùng học cách bỏ qua.
     */
    const sameInstant = (draft, applied) => {
        if (!draft) return !applied
        if (!applied) return false

        return draft.isSame(moment(applied))
    }

    const isDirty = draftActor !== (query.actor || '')
        || draftStageCode !== (query.stageCode || '')
        || !sameInstant(draftFrom, query.from)
        || !sameInstant(draftTo, query.to)

    const applyFilters = () => {
        // KHÔNG bắt buộc tài khoản: khoan sâu từ dòng cha của màn gộp theo công đoạn là ca
        // "một công đoạn, MỌI tài khoản" — hợp lệ, và `actorLabel` đã có nhánh hiển thị cho nó.
        // Vẫn phải có ít nhất một trong hai, nếu không là lấy toàn bộ log của cả khoảng.
        if (!draftActor && !draftStageCode) {
            message.error('Chọn tài khoản hoặc công đoạn trước khi áp dụng')

            return
        }

        if (!draftFrom || !draftTo || draftFrom.isAfter(draftTo)) {
            message.error('Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc')

            return
        }

        /*
         * ⚠️ Gửi biên NGÀY VN như mọi đường khác của module — đây là đường thứ SÁU mà lượt "gom
         *    năm đường về một chỗ" bỏ sót. Để `toISOString()` ở đây là modal tự gửi một khoảng
         *    khác khoảng trang cha vừa gửi, trên cùng một cú bấm.
         */
        const vnRange = vnDayRangeIso(draftFrom, draftTo)
        const next = {
            actor: draftActor || undefined,
            stageCode: draftStageCode || undefined,
            from: vnRange.from,
            to: vnRange.to,
        }

        if (embedded) {
            onApplyFilters(next)
        } else {
            history.push(`${location.pathname}?${queryString.stringify(next)}`)
        }
    }

    /**
     * Thiếu `from`/`to` là chuyện đường dẫn khi mở thẳng route độc lập không tham số (embedded LUÔN
     * có sẵn từ báo cáo truyền sang, không rơi vào nhánh này) — nói đúng như vậy và chỉ ra đường quay
     * lại, thay vì để người dùng nhìn "from must be a valid ISO 8601 date string" và tưởng service
     * hỏng. Không đoán khoảng mặc định: đây là phần khoan sâu của một ô đã bấm, đoán khoảng khác là
     * hiện ra những con số KHÔNG khớp con số họ vừa bấm vào.
     */
    const missingRange = !query.from || !query.to

    const fetchRecords = useCallback(async (page = 1) => {
        if (missingRange) {
            setLoading(false)

            return
        }

        setLoading(true)
        setError(null)

        try {
            const result = await getOutputRecords({
                from: query.from,
                to: query.to,
                actor: query.actor,
                stageCode: query.stageCode,
                only: only === 'distinct' ? undefined : only,
                groupBy: groupByBarcode ? 'barcode' : undefined,
                page,
            })

            setRows(result.data || [])
            setMeta(result.meta || null)
            setPagination(result.pagination || {page, limit: 50, total: 0})
        } catch (e) {
            setError(e)
        } finally {
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [missingRange, only, groupByBarcode, query.from, query.to, query.actor, query.stageCode])

    useEffect(() => {
        fetchRecords(1)
    }, [fetchRecords])

    const stage = (meta && meta.stage) || (rows[0] || {})
    const byOrder = stage.countUnit === 'order'
    const byDetect = stage.timeBasis === 'by_detect'

    /** Vào từ dòng tổng (không có stageCode) thì bảng có thêm cột Công đoạn (C-AC-07). */
    const fromTotalRow = !query.stageCode

    const statusTag = (status) => {
        const label = OUTPUT_STATUS_LABELS[status] || {}

        return <Tag color={label.color}>{label.text}</Tag>
    }

    const itemColumns = [
        {
            title: 'Barcode',
            dataIndex: 'barcode',
            width: 150,
            ellipsis: true,
            render: (value) => <code className="VcBarcode">{value}</code>,
        },
        {title: 'Đơn hàng', dataIndex: 'orderId', width: 110, ellipsis: true},
        {title: 'SKU', dataIndex: 'sku', render: (value) => <code>{value}</code>},
        ...(fromTotalRow ? [{title: 'Công đoạn', dataIndex: 'stageName', width: 120}] : []),
        ...(byDetect ? [{
            title: 'Vật liệu & đại lượng dùng để tính giờ',
            key: 'materialLines',
            width: 250,
            render: (text, row) => (row.materialLines || []).map((line) => (
                <div key={line.materialCode}>
                    <code>{line.materialCode}</code> {line.materialName} ·{' '}
                    {formatInt(line.quantity)} {line.unit}
                </div>
            )),
        }] : []),
        {title: 'Thời gian', dataIndex: 'logTime', width: 150, render: (value) => <TimeAgo value={value}/>},
        {
            title: 'Lần sản xuất',
            dataIndex: 'seq',
            width: 100,
            align: 'center',
            // Số THẬT, không còn tiền tố "lần" — bảng đã tô màu VcRow--redo cho dòng làm lại rồi,
            // in lại chữ "làm lại" ở đây là trùng thông tin và làm cột không còn width cố định.
            render: (seq) => (seq > 1 ? <strong>{seq}</strong> : seq),
        },
        {
            title: 'Sản lượng',
            dataIndex: 'output',
            align: 'right',
            width: 90,
            render: (value) => <strong>{formatInt(value)}</strong>,
        },
        {
            title: 'Giờ định mức',
            dataIndex: 'standardSeconds',
            align: 'right',
            width: 130,
            render: (value) => <Duration seconds={value}/>,
        },
        {title: 'Trạng thái', dataIndex: 'status', width: 150, render: statusTag},
    ]

    const orderColumns = [
        {
            title: 'Đơn hàng',
            dataIndex: 'orderId',
            width: 120,
            ellipsis: true,
            render: (value) => <strong>{value}</strong>,
        },
        {
            // Cột quan trọng nhất của cấu trúc 2: không có nó thì người xem thấy 6 dòng log biến
            // thành 1 sản lượng mà không hiểu vì sao, và sẽ nghi báo cáo đếm thiếu.
            title: 'Barcode đã gộp',
            key: 'mergedBarcodes',
            render: (text, row) => (
                <div>
                    {(row.mergedBarcodes || []).map((barcode) => (
                        <code className="VcBarcode" key={barcode}>{barcode}</code>
                    ))}
                    <span className="VcMuted"> ({formatInt(row.logCount)} dòng log)</span>
                </div>
            ),
        },
        {title: 'SKU', dataIndex: 'sku', render: (value) => <code>{value}</code>},
        {title: 'Thời gian', dataIndex: 'logTime', width: 150, render: (value) => <TimeAgo value={value}/>},
        {
            title: 'Sản lượng',
            dataIndex: 'output',
            align: 'right',
            width: 90,
            render: (value) => <strong>{formatInt(value)}</strong>,
        },
        {
            title: 'Giờ định mức',
            dataIndex: 'standardSeconds',
            align: 'right',
            width: 130,
            render: (value) => <Duration seconds={value}/>,
        },
        {title: 'Trạng thái', dataIndex: 'status', width: 150, render: statusTag},
    ]

    const explainKey = only && EXPLAIN[only]
        ? only
        : byOrder ? 'by_order' : byDetect ? 'by_detect' : null
    const explain = explainKey ? EXPLAIN[explainKey] : null

    return (
        <div className="VcPage VcOutputDetailPage container-fluid">
            <div className="VcPageHead">
                <div>
                    <h1 className="VcPageTitle">Chi tiết sản lượng</h1>
                    <div className="VcPageSubtitle">
                        {actorLabel}
                        {stage.name ? ` · ${stage.name}` : ''}
                        {` · ${formatInt(pagination.total)} dòng`}
                        {query.from && ` · ${formatDateTime(query.from)} → ${formatDateTime(query.to)} (${timezoneLabelForRange(query.from, query.to)})`}
                    </div>
                </div>
                {/*
                  * MỘT đường đóng duy nhất. Modal đã tắt nút `×` của antd (`closable={false}`), nên
                  * nút này là lối ra — trước đó có cả hai, hai nút cạnh nhau làm cùng một việc chỉ
                  * khiến người dùng phải dừng lại đoán xem chúng có khác nhau không.
                  */}
                <div className="VcPageActions">
                    {/* C-AC-08: quay lại thì bộ lọc và các dòng đang mở rộng ở báo cáo giữ nguyên. */}
                    <Button onClick={closeAction}>{embedded ? 'Đóng' : '← Quay lại báo cáo'}</Button>
                </div>
            </div>

            {/*
              * Bộ lọc SỬA ĐƯỢC của riêng màn này — mặc định lấy tài khoản/công đoạn/khoảng thời gian
              * do báo cáo truyền sang, bấm "Áp dụng" thì đổi ngay tại đây mà KHÔNG đụng bộ lọc của
              * báo cáo phía sau (embedded: modal vẫn mở, chỉ tự fetch lại theo bộ lọc mới).
              */}
            <div className="VcFilterCard">
                <div className="VcFilterRow">
                    <div className="VcFilterItem">
                        <label>Tài khoản</label>
                        <Select
                            value={draftActor}
                            // Phải có đường QUAY LẠI "mọi tài khoản": khoan sâu từ dòng cha màn gộp
                            // theo công đoạn mở ra ở chính trạng thái đó, thu hẹp về một người rồi
                            // thì không có cách nào về lại — y như ô Công đoạn ngay dưới đã làm.
                            allowClear={false}
                            onChange={setDraftActor}
                            style={{minWidth: 180}}
                        >
                            <Option value="">Tất cả tài khoản</Option>
                            {(actorOptions || (query.actor ? [query.actor] : [])).map((actor) => (
                                <Option key={actor} value={actor}>{actor}</Option>
                            ))}
                        </Select>
                    </div>
                    <div className="VcFilterItem">
                        <label>Công đoạn</label>
                        <Select
                            value={draftStageCode || ''}
                            onChange={setDraftStageCode}
                            style={{minWidth: 200}}
                        >
                            <Option value="">Tất cả công đoạn</Option>
                            {(stageOptions || (stage.stageCode ? [stage] : [])).map((s) => (
                                <Option key={s.stageCode} value={s.stageCode}>{s.name}</Option>
                            ))}
                        </Select>
                    </div>
                    <div className="VcFilterItem">
                        <label>Từ ngày giờ</label>
                        <DatePicker
                            showTime={{format: 'HH:mm'}}
                            format="DD/MM/YYYY HH:mm"
                            value={draftFrom}
                            onChange={setDraftFrom}
                            style={{width: 175}}
                        />
                    </div>
                    <div className="VcFilterArrow">→</div>
                    <div className="VcFilterItem">
                        <label>Đến ngày giờ</label>
                        <DatePicker
                            showTime={{format: 'HH:mm'}}
                            format="DD/MM/YYYY HH:mm"
                            value={draftTo}
                            onChange={setDraftTo}
                            style={{width: 175}}
                        />
                    </div>
                    <div className="VcFilterItem VcFilterItem--action">
                        <Badge dot={isDirty} offset={[-6, 6]}>
                            <Button type="primary" onClick={applyFilters}>Áp dụng</Button>
                        </Badge>
                        {/* Chữ kèm dấu chấm đỏ trên nút: chỉ dấu chấm thì dễ bị bỏ qua, nhất là màn
                          * hình chất lượng thấp tại xưởng (AC-UI-01, cùng nguyên tắc không chỉ dùng màu). */}
                        {isDirty && <span className="VcMuted VcFilterDirtyHint">Có thay đổi chưa áp dụng</span>}
                    </div>
                </div>
            </div>

            <div className="VcChipRow">
                {/* Thẻ tài khoản · công đoạn KHÔNG có nút gỡ (C-AC-04). */}
                <Tag>{actorLabel}{stage.name ? ` · ${stage.name}` : ''}</Tag>

                {groupByBarcode && (
                    <Tag closable onClose={() => setOnly('')}>
                        Gộp theo barcode — mỗi sản phẩm một dòng
                    </Tag>
                )}
                {only === 'redo' && (
                    <Tag closable onClose={() => setOnly('')}>Chỉ sản phẩm phải làm lại</Tag>
                )}
                {only === 'no_standard' && (
                    <Tag color="orange" closable onClose={() => setOnly('')}>
                        Chỉ sản lượng thiếu định mức — danh sách IE cần bổ sung
                    </Tag>
                )}
                {only === 'no_file_data' && (
                    <Tag color="red" closable onClose={() => setOnly('')}>
                        Chỉ sản lượng thiếu dữ liệu file in — danh sách Designer cần xử lý
                    </Tag>
                )}
            </div>

            {explain && <Notice level={explain.level}>{explain.text}</Notice>}

            {/*
              * Chế độ "mọi tài khoản" trộn log của nhiều người mà bảng KHÔNG có cột nào phân biệt:
              * `detailRowOf` phía service (`output-control.service.ts:403-420`) không trả `actor`,
              * nên không thêm cột được từ FE — thêm `dataIndex="actor"` chỉ ra một cột rỗng.
              *
              * Nói thẳng giới hạn thay vì để người dùng tự suy ra từ một bảng không có dấu hiệu gì.
              * Gỡ dòng này khi service bổ sung `actor` vào `DetailRow` (nguồn đã có sẵn:
              * `OutputRecord.actor`) và bảng có cột Tài khoản đối xứng với cột Công đoạn.
              */}
            {!query.actor && !missingRange && !error && (
                <Notice level="warning">
                    Đang xem <strong>mọi tài khoản</strong> — các dòng dưới đây thuộc nhiều nhân viên
                    khác nhau và bảng chưa có cột phân biệt.
                    {/*
                      * Chỉ mời chọn tài khoản khi THẬT SỰ chọn được. `actorOptions` chỉ được truyền ở
                      * nhánh nhúng (`OutputReportPage.js:1155`); mở thẳng đường dẫn
                      * `/control/output/detail` thì nó `undefined` và dự phòng là
                      * `(query.actor ? [query.actor] : [])` — mà điều kiện của chính Notice này là
                      * `!query.actor`, nên danh sách LUÔN rỗng. Mời họ bấm vào một ô chỉ có mỗi
                      * "Tất cả tài khoản" là đẩy vào ngõ cụt.
                      */}
                    {(actorOptions || []).length > 0 && ' Chọn một tài khoản ở ô lọc phía trên để xem riêng từng người.'}
                </Notice>
            )}

            {missingRange ? (
                /*
                 * Thiếu `from`/`to` là chuyện đường dẫn, không phải lỗi hệ thống — nói đúng như vậy
                 * và chỉ ra đường quay lại, thay vì để người dùng nhìn "from must be a valid ISO 8601
                 * date string" và tưởng service hỏng.
                 */
                <Notice level="warning" action="Về báo cáo sản lượng" onAction={() => history.push(ROUTES.OUTPUT_REPORT)}>
                    Thiếu khoảng thời gian nên chưa lấy được dữ liệu. Màn hình này là phần xem sâu của
                    một ô trên báo cáo sản lượng, nên khoảng thời gian đi kèm từ đó sang — mở thẳng
                    đường dẫn hoặc tải lại trang sau khi mất tham số thì không còn khoảng nào để tra.
                    Quay về báo cáo và bấm vào ô cần xem.
                </Notice>
            ) : error ? (
                <LoadError error={error} onRetry={() => fetchRecords(pagination.page)}/>
            ) : (
                <>
                    <Table
                        className="VcTable"
                        rowKey={(row) => `${row.barcode}-${row.logTime}-${row.seq}`}
                        columns={byOrder ? orderColumns : itemColumns}
                        dataSource={rows}
                        loading={loading}
                        pagination={false}
                        size="middle"
                        // `tableLayout="fixed"`: mặc định antd chỉ coi `width` là GỢI Ý (table-layout
                        // auto), cột co giãn theo nội dung — Barcode/Đơn hàng/Lần sản xuất/Sản
                        // lượng/Trạng thái vì vậy lệch độ rộng giữa các dòng. Ép `fixed` để các cột đã
                        // khai `width` giữ đúng kích thước, nội dung dài hơn thì cắt (`ellipsis`).
                        tableLayout="fixed"
                        locale={{
                            emptyText: (
                                <div className="VcEmptyState">
                                    <div>Không có dòng nào khớp thẻ lọc đang áp.</div>
                                    <div className="VcHint">Gỡ thẻ lọc để xem toàn bộ danh sách.</div>
                                </div>
                            ),
                        }}
                        rowClassName={(row) => {
                            // Thứ tự ưu tiên màu: thiếu file in → thiếu định mức → lần làm lại.
                            // Một dòng vừa làm lại vừa thiếu dữ liệu thì lấy màu đỏ.
                            if (row.status === 'no_file_data') return 'VcRow--noFileData'
                            if (row.status === 'no_standard') return 'VcRow--noStandard'
                            if (row.seq > 1) return 'VcRow--redo'

                            return ''
                        }}
                    />

                    <div className="VcPaginationBar">
                        <span className="VcMuted">
                            Hiển thị {formatInt((pagination.page - 1) * pagination.limit + (rows.length ? 1 : 0))}
                            –{formatInt((pagination.page - 1) * pagination.limit + rows.length)} trong{' '}
                            {formatInt(pagination.total)} dòng
                        </span>
                        <Pagination
                            current={pagination.page}
                            pageSize={pagination.limit}
                            total={pagination.total}
                            onChange={fetchRecords}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

OutputDetailPage.propTypes = {
    /** Mở dạng modal NGAY TRÊN url của báo cáo thay vì điều hướng sang route riêng. */
    embedded: PropTypes.bool,
    /** Tham số điều hướng khi `embedded` — ở chế độ route thì đọc từ `location.search`. */
    params: PropTypes.object,
    onClose: PropTypes.func,
    /** Đẩy bộ lọc của modal ngược lên màn báo cáo cha. */
    onApplyFilters: PropTypes.func,
    stageOptions: PropTypes.array,
    actorOptions: PropTypes.array,
}

OutputDetailPage.defaultProps = {
    embedded: false,
    params: {},
    onClose: () => undefined,
    // Callback nào cũng no-op, không `null`: cả hai chỉ được gọi ở nhánh `embedded`, nhưng `null`
    // thì embedder quên truyền là `TypeError` ngay — khuôn `SuspectPage`/`GlossaryPage` dùng no-op.
    onApplyFilters: () => undefined,
    /*
     * ⚠️ CỐ Ý không đặt mặc định cho `stageOptions` / `actorOptions`.
     *
     * Hai chỗ dùng chúng là `(actorOptions || (query.actor ? [query.actor] : []))` và
     * `(stageOptions || (stage.stageCode ? [stage] : []))` — mảng rỗng là TRUTHY nên đặt `[]` làm
     * mặc định sẽ giết nhánh dự phòng. Ở chế độ route (`/control/output/detail`) React Router chỉ
     * truyền `history`/`location`/`match`, nên dropdown "Tài khoản" thành rỗng trắng và ai lỡ chọn
     * "Tất cả công đoạn" là không còn đường quay lại. Đo ở vòng review 15.
     */
}

export default OutputDetailPage
